// MemberUpgrade.tsx - Section amélioration du niveau de membre
import React, { useState } from 'react';
import { MembershipLevel } from '../../types/adhesion.types';
import {
    ArrowUp,
    Shield,
    Star,
    User,
    CheckCircle,
    Info,
    AlertTriangle,
    Phone,
    Mail,
    X
} from 'lucide-react';

interface MemberUpgradeProps {
    memberData: any;
    currentLevel: MembershipLevel;
}

const MemberUpgrade: React.FC<MemberUpgradeProps> = ({ memberData, currentLevel }) => {
    const [showNipInfo, setShowNipInfo] = useState(false);

    // Définir les avantages par niveau
    const levelBenefits = {
        sympathisant: {
            icon: <User className="w-6 h-6 text-blue-600" />,
            label: 'Sympathisant',
            description: 'Soutien de base au mouvement',
            color: 'border-blue-200 bg-blue-50',
            benefits: [
                'Newsletter et actualités',
                'Invitations aux événements publics',
                'Communauté en ligne',
                'Code de parrainage personnel'
            ]
        },
        partial_member: {
            icon: <Star className="w-6 h-6 text-yellow-600" />,
            label: 'Membre Partiel',
            description: 'Engagement renforcé sans NIP',
            color: 'border-yellow-200 bg-yellow-50',
            benefits: [
                'Tous les avantages sympathisant',
                'Accès à l\'espace membre privé',
                'Documents internes',
                'Participation aux discussions',
                'Formation et webinaires',
                'Upgrade automatique avec NIP'
            ]
        },
        full_member: {
            icon: <Shield className="w-6 h-6 text-green-600" />,
            label: 'Membre Complet',
            description: 'Tous les privilèges et droits',
            color: 'border-green-200 bg-green-50',
            benefits: [
                'Tous les avantages membre partiel',
                'Carte de membre officielle',
                'QR code personnel',
                'Droit de vote aux assemblées',
                'Documents officiels confidentiels',
                'Opportunités de leadership',
                'Mentions spéciales',
                'Priorité aux formations'
            ]
        }
    };

    // Déterminer le prochain niveau
    const getNextLevel = (): MembershipLevel | null => {
        switch (currentLevel) {
            case 'sympathisant': return 'partial_member';
            case 'partial_member': return 'full_member';
            default: return null;
        }
    };

    // Étapes pour passer au niveau supérieur
    const getUpgradeSteps = () => {
        const nextLevel = getNextLevel();
        
        if (currentLevel === 'sympathisant' && nextLevel === 'partial_member') {
            return {
                title: 'Devenir Membre Partiel',
                description: 'Renforcez votre engagement en complétant votre profil',
                steps: [
                    {
                        id: 'email',
                        title: 'Ajouter votre email',
                        description: 'Nécessaire pour recevoir les communications internes',
                        completed: !!memberData.email,
                        action: 'Aller au profil',
                        actionUrl: '#profile'
                    },
                    {
                        id: 'profession',
                        title: 'Renseigner votre profession',
                        description: 'Aide à mieux comprendre notre communauté',
                        completed: !!memberData.profession,
                        action: 'Compléter',
                        actionUrl: '#profile'
                    },
                    {
                        id: 'engagement',
                        title: 'Confirmer votre engagement',
                        description: 'Accepter les responsabilités de membre partiel',
                        completed: false,
                        action: 'Confirmer',
                        actionUrl: '#confirm'
                    }
                ]
            };
        }
        
        if (currentLevel === 'partial_member' && nextLevel === 'full_member') {
            return {
                title: 'Devenir Membre Complet',
                description: 'Obtenez votre NIP pour débloquer tous les privilèges',
                steps: [
                    {
                        id: 'nip-info',
                        title: 'Comprendre le NIP',
                        description: 'Apprendre ce qu\'est le NIP et pourquoi il est nécessaire',
                        completed: true,
                        action: 'En savoir plus',
                        actionUrl: '#nip-info'
                    },
                    {
                        id: 'get-nip',
                        title: 'Obtenir votre NIP',
                        description: 'Faire votre CNI ou mettre à jour vos informations',
                        completed: !!memberData.nip,
                        action: 'Guide CNI',
                        actionUrl: '#cni-guide'
                    },
                    {
                        id: 'update-profile',
                        title: 'Mettre à jour votre profil',
                        description: 'Ajouter votre NIP dans votre profil membre',
                        completed: !!memberData.nip,
                        action: 'Ajouter NIP',
                        actionUrl: '#add-nip'
                    }
                ]
            };
        }

        return null;
    };

    const nextLevel = getNextLevel();
    const upgradeSteps = getUpgradeSteps();
    const currentLevelData = levelBenefits[currentLevel];
    const nextLevelData = nextLevel ? levelBenefits[nextLevel] : null;

    // Si déjà membre complet
    if (!nextLevel) {
        return (
            <div className="space-y-6">
                <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                    <div className="text-center">
                        <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Shield className="w-10 h-10 text-white" />
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">
                            Félicitations ! 🎉
                        </h1>
                        <p className="text-gray-600 text-lg">
                            Vous êtes déjà membre complet de l'UDB
                        </p>
                        <p className="text-gray-500 mt-2">
                            Vous bénéficiez de tous les privilèges et avantages disponibles.
                        </p>
                    </div>
                </div>

                <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6 border border-green-200">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                        Vos privilèges de membre complet
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {levelBenefits.full_member.benefits.map((benefit, index) => (
                            <div key={index} className="flex items-center space-x-2">
                                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                                <span className="text-gray-700 text-sm">{benefit}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-green-100 rounded-lg">
                        <ArrowUp className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Améliorer mon niveau</h1>
                        <p className="text-gray-600">Débloquez plus d'avantages et de privilèges</p>
                    </div>
                </div>

                {/* Progression actuelle */}
                <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-600">Niveau actuel</span>
                        <span className="text-sm font-medium text-gray-600">Niveau suivant</span>
                    </div>
                    <div className="flex items-center space-x-4">
                        <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg border-2 ${currentLevelData.color}`}>
                            {currentLevelData.icon}
                            <span className="font-semibold">{currentLevelData.label}</span>
                        </div>
                        <ArrowUp className="w-5 h-5 text-gray-400" />
                        {nextLevelData && (
                            <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg border-2 border-dashed ${nextLevelData.color}`}>
                                {nextLevelData.icon}
                                <span className="font-semibold">{nextLevelData.label}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Étapes d'amélioration */}
            {upgradeSteps && (
                <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                        {upgradeSteps.title}
                    </h2>
                    <p className="text-gray-600 mb-6">{upgradeSteps.description}</p>

                    <div className="space-y-4">
                        {upgradeSteps.steps.map((step, index) => (
                            <div key={step.id} className={`border rounded-lg p-4 ${step.completed ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-white'}`}>
                                <div className="flex items-start space-x-3">
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                        step.completed ? 'bg-green-100' : 'bg-gray-100'
                                    }`}>
                                        {step.completed ? (
                                            <CheckCircle className="w-4 h-4 text-green-600" />
                                        ) : (
                                            <span className="text-xs font-bold text-gray-600">{index + 1}</span>
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <h3 className={`font-semibold ${step.completed ? 'text-green-800' : 'text-gray-900'}`}>
                                            {step.title}
                                        </h3>
                                        <p className={`text-sm mt-1 ${step.completed ? 'text-green-700' : 'text-gray-600'}`}>
                                            {step.description}
                                        </p>
                                    </div>
                                    {!step.completed && (
                                        <button
                                            onClick={() => {
                                                if (step.actionUrl === '#nip-info') {
                                                    setShowNipInfo(true);
                                                }
                                            }}
                                            className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors font-medium"
                                        >
                                            {step.action}
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Comparaison des avantages */}
            {nextLevelData && (
                <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900 mb-6">
                        Ce que vous gagnez en passant au niveau supérieur
                    </h2>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Niveau actuel */}
                        <div className={`border rounded-lg p-4 ${currentLevelData.color}`}>
                            <div className="flex items-center space-x-2 mb-3">
                                {currentLevelData.icon}
                                <h3 className="font-semibold">{currentLevelData.label} (Actuel)</h3>
                            </div>
                            <ul className="space-y-2">
                                {currentLevelData.benefits.map((benefit, index) => (
                                    <li key={index} className="flex items-center space-x-2 text-sm">
                                        <CheckCircle className="w-3 h-3 text-gray-600" />
                                        <span>{benefit}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Niveau suivant */}
                        <div className={`border-2 border-dashed rounded-lg p-4 ${nextLevelData.color} relative overflow-hidden`}>
                            <div className="absolute top-2 right-2">
                                <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-medium">
                                    Bientôt !
                                </span>
                            </div>
                            <div className="flex items-center space-x-2 mb-3">
                                {nextLevelData.icon}
                                <h3 className="font-semibold">{nextLevelData.label}</h3>
                            </div>
                            <ul className="space-y-2">
                                {nextLevelData.benefits.map((benefit, index) => (
                                    <li key={index} className={`flex items-center space-x-2 text-sm ${
                                        currentLevelData.benefits.includes(benefit) ? 'opacity-60' : 'font-medium'
                                    }`}>
                                        <CheckCircle className="w-3 h-3 text-gray-600" />
                                        <span>{benefit}</span>
                                        {!currentLevelData.benefits.includes(benefit) && (
                                            <span className="text-green-600 text-xs font-bold">NOUVEAU</span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            )}

            {/* Guide pour obtenir un NIP */}
            {currentLevel === 'partial_member' && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                    <div className="flex items-start space-x-3">
                        <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
                        <div>
                            <h3 className="text-lg font-semibold text-yellow-800 mb-2">
                                Comment obtenir votre NIP ?
                            </h3>
                            <p className="text-yellow-700 mb-4">
                                Le NIP est nécessaire pour devenir membre complet. Voici les étapes :
                            </p>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="bg-white p-4 rounded-lg border border-yellow-200">
                                    <h4 className="font-semibold text-yellow-800 mb-2">🇬🇦 Au Gabon</h4>
                                    <ul className="text-sm text-yellow-700 space-y-1">
                                        <li>• Rendez-vous au centre CNI le plus proche</li>
                                        <li>• Apportez vos documents (acte de naissance, photos)</li>
                                        <li>• Coût : 25,000 FCFA</li>
                                        <li>• Délai : 2-3 semaines</li>
                                    </ul>
                                </div>
                                <div className="bg-white p-4 rounded-lg border border-yellow-200">
                                    <h4 className="font-semibold text-yellow-800 mb-2">🌍 À l'étranger</h4>
                                    <ul className="text-sm text-yellow-700 space-y-1">
                                        <li>• Contactez le consulat du Gabon</li>
                                        <li>• Prenez rendez-vous pour la CNI</li>
                                        <li>• Coût variable selon le pays</li>
                                        <li>• Délai : 4-6 semaines</li>
                                    </ul>
                                </div>
                            </div>

                            <div className="mt-4 flex flex-col sm:flex-row gap-3">
                                <a
                                    href="tel:+241XXXXXXXX"
                                    className="flex items-center justify-center space-x-2 bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors font-medium"
                                >
                                    <Phone className="w-4 h-4" />
                                    <span>Appeler le support</span>
                                </a>
                                <button
                                    onClick={() => setShowNipInfo(true)}
                                    className="flex items-center justify-center space-x-2 bg-white text-yellow-700 border border-yellow-300 px-4 py-2 rounded-lg hover:bg-yellow-50 transition-colors font-medium"
                                >
                                    <Info className="w-4 h-4" />
                                    <span>Plus d'infos</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal d'informations sur le NIP */}
            {showNipInfo && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg p-6 max-w-lg w-full max-h-96 overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">Tout savoir sur le NIP</h3>
                            <button
                                onClick={() => setShowNipInfo(false)}
                                className="p-1 hover:bg-gray-100 rounded"
                            >
                                <X className="w-5 h-5 text-gray-400" />
                            </button>
                        </div>
                        
                        <div className="space-y-4 text-sm text-gray-700">
                            <p>
                                <strong>Le NIP (Numéro d'Identification Personnelle)</strong> est l'identifiant unique 
                                de chaque citoyen gabonais, inscrit sur la Carte Nationale d'Identité.
                            </p>
                            <p>
                                Selon la législation gabonaise, seuls les citoyens avec un NIP valide peuvent 
                                être membres complets d'un parti politique.
                            </p>
                            <div>
                                <h4 className="font-semibold mb-2">Centres CNI au Gabon :</h4>
                                <ul className="list-disc pl-5 space-y-1">
                                    <li>Libreville : Centre ville, Akanda, Owendo</li>
                                    <li>Port-Gentil : Centre administratif</li>
                                    <li>Franceville : Préfecture</li>
                                    <li>Oyem : Centre administratif</li>
                                </ul>
                            </div>
                            <div>
                                <h4 className="font-semibold mb-2">Documents nécessaires :</h4>
                                <ul className="list-disc pl-5 space-y-1">
                                    <li>Acte de naissance intégral</li>
                                    <li>2 photos d'identité récentes</li>
                                    <li>Attestation de résidence</li>
                                    <li>25,000 FCFA (frais de dossier)</li>
                                </ul>
                            </div>
                        </div>

                        <div className="mt-6 flex space-x-3">
                            <a
                                href="mailto:support@udb.ga"
                                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                            >
                                <Mail className="w-4 h-4" />
                                <span>Contacter le support</span>
                            </a>
                            <button
                                onClick={() => setShowNipInfo(false)}
                                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm"
                            >
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Support et aide */}
            <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-sm font-semibold text-gray-900">Besoin d'aide ?</h3>
                        <p className="text-sm text-gray-600">Notre équipe est là pour vous accompagner</p>
                    </div>
                    <a
                        href="mailto:support@udb.ga"
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                    >
                        Contacter le support
                    </a>
                </div>
            </div>
        </div>
    );
};

export default MemberUpgrade;